<template>
  <div ref="popover" class="label-popover-wrapper">
    <span
        @click="togglePopover"
        class="label-button relative"
        :class="{ active: popoverVisible }"
        title="Gerenciar tags"
    >
      <bookmark-icon size="1x" class="mb-1" />
    </span>

    <div v-if="popoverVisible" ref="panel" class="label-popover" :style="popoverPosition">
      <button @click="toggleCreation" class="btn btn-link float-right">
        {{ enableCreation ? 'Cancelar' : 'Criar nova tag' }}
      </button>

      <div v-show="!enableCreation">
        <h5 class="mt-2 mb-3">Tags</h5>

        <!-- Search -->
        <div class="mt-2">
          <div v-if="tags.length">
            <p class="mt-3">Selecione as tags que deseja vincular:</p>
            <input v-model="generic" class="input form-control mb-3" placeholder="Pesquisar tag">
          </div>
          <div v-else>
            <p class="mt-3">Nenhuma tag encontrada.</p>
          </div>
        </div>

        <!-- Show Tags -->
        <div class="table-container">
          <table class="table">
            <tbody>
              <tr v-for="(tag, index) in showTags" :key="tag.id">
                <td>
                  <input
                      type="checkbox"
                      v-model="tag.enabled"
                      :value="tag"
                      :id="'tagCheckbox_' + index"
                  >
                </td>
                <td>
                  <span
                      class="color-badge"
                      :style="{ backgroundColor: `#${tag.color}` }"
                  ></span>
                  {{ tag.name }}
                </td>
                <td>
                  <button @click="deleteTag(tag)" class="btn-icon btn-outline-red ml-2 mr-3 float-right">
                    <trash-2-icon size="1.5x"/>
                  </button>
                  <button @click="editTag(tag)" class="btn-icon float-right" title="Editar tag">
                    <edit-2-icon size="1.5x"/>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <hr>

        <div class="text-center">
          <button @click="associateTags" class="btn btn-primary btn-block">Salvar</button>
          <button @click="togglePopover" class="btn btn-link">Fechar</button>
        </div>
      </div>

      <!-- Create/Edit Tag -->
      <div v-show="enableCreation">
        <h5 class="mt-2 mb-3">{{ editing ? 'Editar tag' : 'Nova tag' }}</h5>
        <div class="mb-3">
          <label for="tag-name">Escolha um nome:</label>
          <input id="tag-name" v-model="newTag.name" type="text" class="form-control" placeholder="Exemplo: VIP, SNE 20, SNE 40...">
        </div>
        <div class="mb-3">
          <div class="form-group">
            <label for="tag-color">Selecione uma cor:</label>
            <div style="display: flex; flex-wrap: wrap; gap: 6px;">
              <button
                  v-for="color in colorOptions"
                  @click="selectColor(color)"
                  :key="color.value"
                  :title="color.label"
                  class="btn btn-sm p-0"
                  :style="{
                    backgroundColor: `#${color.value}`,
                    width: '26px',
                    height: '26px',
                    outline: newTag.kind === color.kind ? '2px solid #051B30' : 'none',
                    outlineOffset: '1px'
                  }"
              >
              </button>
            </div>
          </div>
        </div>
        <div>
          <button
            :disabled="disabledSaveButton"
            class="btn btn-primary btn-block"
            @click="saveTag"
          >
            {{ editing ? 'Salvar tag' : 'Criar tag' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const tagPath = 'admin/account_tags'

import { Trash2Icon, BookmarkIcon, Edit2Icon } from 'vue-feather-icons'
import { TAG_COLOR_OPTIONS } from '@/utils/accountTags'

const EDGE_GAP = 12

export default {
  name: 'AccountTagsPopover',
  components: {
    Trash2Icon,
    BookmarkIcon,
    Edit2Icon
  },
  props: {
    tags: { type: Array, required: true },
    tagIds: { type: Array, required: true }
  },
  data: () => ({
    generic: '',
    popoverVisible: false,
    newTag: {},
    editing: null,
    cloneTags: [],
    enableCreation: false,
    popoverPosition: {}
  }),
  computed: {
    colorOptions() {
      return TAG_COLOR_OPTIONS
    },
    disabledSaveButton() {
      return !this.newTag.kind || !this.newTag.name || this.newTag.name.trim().length < 3
    },
    showTags() {
      const filtered = this.generic
        ? this.cloneTags.filter(tag => tag.name.toLowerCase().includes(this.generic.toLowerCase()))
        : this.cloneTags

      return filtered.slice().sort((a, b) => {
        const nameA = a.name.toLowerCase()
        const nameB = b.name.toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })
    }
  },
  methods: {
    associateTags() {
      this.$emit('associateTags', this.cloneTags.filter(tag => tag.enabled).map(tag => tag.id))
      this.closePopover()
    },
    selectColor(color) {
      this.$set(this.newTag, 'kind', color.kind)
    },
    setCloneTags() {
      this.cloneTags = this.tags.map(tag => ({
        ...tag,
        enabled: this.tagIds.includes(tag.id)
      }))
      this.cloneTags.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
    },
    togglePopover() {
      this.popoverVisible = !this.popoverVisible
      if (this.popoverVisible) {
        this.setCloneTags()
        this.enableCreation = false
        this.$nextTick(() => {
          this.updatePosition()
          document.addEventListener('click', this.handleClickOutside)
          window.addEventListener('resize', this.updatePosition)
        })
      }
    },
    updatePosition() {
      const panel = this.$refs.panel

      if (!panel)
        return

      const rect = this.$refs.popover.getBoundingClientRect()
      let top = rect.bottom + 5

      if (top + panel.offsetHeight > window.innerHeight)
        top = rect.top - panel.offsetHeight - 5

      const maxRight = Math.max(window.innerWidth - panel.offsetWidth - EDGE_GAP, EDGE_GAP)

      this.popoverPosition = {
        top: Math.max(top, EDGE_GAP) + 'px',
        right: Math.min(Math.max(window.innerWidth - rect.right, EDGE_GAP), maxRight) + 'px'
      }
    },
    closePopover() {
      this.popoverVisible = false
      this.cleanData()
      document.removeEventListener('click', this.handleClickOutside)
      window.removeEventListener('resize', this.updatePosition)
    },
    cleanData() {
      this.enableCreation = false
      this.newTag = {}
      this.editing = null
      this.generic = ''
    },
    editTag(tag) {
      this.editing = tag.id
      this.newTag = { name: tag.name, kind: tag.kind }
      this.enableCreation = true
    },
    saveTag() {
      const params = { name: this.newTag.name.trim(), kind: this.newTag.kind }
      const request = this.editing
        ? this.axios.put(`${tagPath}/${this.editing}`, params)
        : this.axios.post(tagPath, params)

      request
        .then(response => response.data)
        .then(tag => this.editing ? this.replaceTag(tag) : this.tags.push(tag))
        .then(() => this.$emit('changeTags'))
        .then(() => this.cleanData())
        .then(() => this.setCloneTags())
        .catch(e => this.$notify.addError(e))
    },
    replaceTag(tag) {
      const index = this.tags.findIndex(t => t.id === tag.id)
      if (index !== -1) this.$set(this.tags, index, tag)
    },
    handleClickOutside(event) {
      if (this.$refs.popover && !this.$refs.popover.contains(event.target)) {
        this.closePopover()
      }
    },
    toggleCreation() {
      this.enableCreation = !this.enableCreation
      if (!this.enableCreation) this.cleanData()
    },
    deleteTag(tag) {
      if (!confirm(`Atenção! \n\nA tag ${tag.name.toUpperCase()} será removida e desvinculada de todas as contas. \n\nDeseja continuar?`))
        return

      this.axios.delete(`${tagPath}/${tag.id}`)
        .then(() => this.removeTag(tag))
        .then(() => this.$emit('deletedTag'))
        .catch(e => this.$notify.addError(e))
    },
    removeTag(tag) {
      const index = this.tags.findIndex(t => t.id === tag.id)
      if (index !== -1) this.tags.splice(index, 1)
      this.setCloneTags()
      this.$emit('changeTags')
    }
  }
}
</script>

<style scoped>
.label-popover {
  position: fixed;
  z-index: 1000;
  background-color: var(--white-bg);
  color: var(--primary-text-color);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  padding: 10px;
  width: 400px;
  max-width: calc(100vw - 24px);
  max-height: min(470px, calc(100vh - 24px));
  overflow-y: auto;
}

.label-popover h5,
.label-popover p,
.label-popover label {
  color: var(--primary-text-color);
}

.label-popover table {
  color: var(--primary-text-color);
}

.color-badge {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 4px;
  border-radius: 3px;
  vertical-align: middle;
}

.table-container {
  max-height: 200px;
  overflow-y: auto;
}

.label-button {
  cursor: pointer;
  padding: 2px 6px 2px 6px;
  background: grey;
  color: white;
  border-radius: 3px;
  font-size: 16px;
}

.label-button:hover {
  background: #ccc;
}
</style>
